import * as Yup from "yup";
import { FormikProvider, getIn, useFormik } from "formik";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { useContext, useEffect, useMemo, useState } from "react";
import InputFormik from "../../../../common/components/form/InputFormik";
import {
  VEHICLE_FUEL,
  IVehicle,
  emptyVehicle,
} from "../../../../types/vehicle.type";
import {
  maskChassi,
  maskCurrency,
  maskLicensePlate,
  maskRenavam,
} from "../../../../common/helpers/mask.helpers";
import { bindVehicleWithVelotrack } from "../../../../common/services/velotrack.api";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { updateVehicle } from "../../../../common/services/vehicles.api";
import { FipeBrandFieldFormik } from "./custom-fields/FipeBrandFieldFormik";
import { FipeModelFieldFormik } from "./custom-fields/FipeModelFieldFormik";
import { FipeYearModelFieldFormik } from "./custom-fields/FipeYearModelFieldFormik";
import { bindFipeData } from "../../../../common/services/fipe.api";
import TextareaFormik from "../../../renters/components/forms/TextareaFormik";
import { getCompanies } from "../../../../common/services/company.api";
import { ICompanyAccess } from "../../../../types/companyaccess.type";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import IlevaActivate from "@/modules/renters/components/modals/fields/UploadInfoApis/IlevaActivate";

const validationSchema = Yup.object().shape({
  licensePlate: Yup.string().required("Placa do veículo é obrigatória"),
  chassi: Yup.string().required("Número do chassi é obrigatório"),
  renavam: Yup.string().required("Renavam é obrigatório"),
  color: Yup.string().required("Cor do veículo é obrigatória"),
  fuel: Yup.string().required("Tipo de combustível é obrigatório"),
  vehicleType: Yup.number().required("Tipo de veículo é obrigatório"),
  year: Yup.string().required("Ano de fabricação é obrigatório"),
  brand: Yup.string().required("Marca do veículo é obrigatória"),
  model: Yup.string().required("Modelo do veículo é obrigatório"),
  yearModel: Yup.string().required("Ano modelo é obrigatório"),
  motorNumber: Yup.string().required("Número do motor é obrigatório"),
  fipeValue: Yup.string(),
  document: Yup.string(),
  fipeInfo: Yup.object({
    fipeVehicleCode: Yup.string().required("é obrigatório buscar código FIPE"),
  }),
});

type FormPropsCreateRenter = {
  onSubmit?: (values) => unknown;
  onSave: (vehicleId: string) => void;
  data?: IVehicle;
};

function FormEditVehicle(props: FormPropsCreateRenter) {
  const authCtx = useContext(AuthContext);
  const userEmail = authCtx.auth?.user?.email;
  const permissions = authCtx.auth?.user?.permissions;

  const { notifySuccess, notifyError } = useToastContext();
  const [bindVelotrackInProgress, setBindVelotrackInProgress] = useState(false);
  const [bindFipeInProgress, setBindFipeInProgress] = useState(false);
  const [companies, setCompanies] = useState<ICompanyAccess[]>([]);
  const [isEligibleSuaVibe, setIsEligibleSuaVibe] = useState(
    props.data?.isEligibleSuaVibe
  );
  // const [rentalPoints, setRentalPoints] = useState<IIndicators[]>([]);

  const formik = useFormik({
    initialValues: Object.assign(emptyVehicle(), props.data as IVehicle),
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      const dataToSave = Object.assign({}, data) as IVehicle;
      data.observations.forEach((observation) => {
        if (!observation.date) {
          observation.date = new Date().toISOString();
        }

        if (!observation.author) {
          // Only set the author if it hasn't been set yet
          observation.author = userEmail || "";
        }
      });

      const value = parseFloat(
        String(dataToSave.purchasePrice)
          .replace("R$", "")
          .replace(/\s/g, "")
          .replace(/\./g, "")
          .replace(",", ".")
      );

      dataToSave.purchasePrice = value;
      const result = await updateVehicle(dataToSave._id, dataToSave);

      if (!result.ok) {
        notifyError(result.message as string);
        return;
      }
      notifySuccess("Atualizado com sucesso");

      props.onSave && props.onSave(data._id);
    },
  });

  const observations = getIn(formik.values, "observations");
  const currentObservation = useMemo(() => {
    const total = observations.filter(({ date }) => !!date);
    return total.length;
  }, [observations]);

  async function handleBindWithVelotrack() {
    try {
      setBindVelotrackInProgress(true);
      const result = await bindVehicleWithVelotrack(formik.values.licensePlate);

      if (!result.ok) {
        notifyError("Veiculo não encontrado na Velotrack");
        return;
      }

      notifySuccess("Veículo encontrado na Velotrack!");

      formik.setFieldValue(
        "velotrackSettings.deviceId",
        (result.data as IVehicle).velotrackSettings.deviceId
      );
    } catch (err) {
      console.log(err);
    } finally {
      setBindVelotrackInProgress(false);
    }
  }

  async function handleBindFipeData() {
    try {
      setBindFipeInProgress(true);
      const result = await bindFipeData(
        formik.values._id,
        formik.values.fipeInfo
      );

      if (!result.ok) {
        notifyError(
          (result.message as string) ||
          "Não foi possível recuperar dados da FIPE, tente novamente"
        );
        return;
      }

      notifySuccess("Dados da FIPE atualizados");
      formik.setFieldValue("fipeInfo", result.data.fipeInfo);
      formik.setFieldValue("brand", result.data.fipeInfo.brand.label);
      formik.setFieldValue("model", result.data.fipeInfo.model.label);
      formik.setFieldValue("yearModel", result.data.fipeInfo.yearModel.label);
    } catch (err) {
      console.log(err);
    } finally {
      setBindFipeInProgress(false);
    }
  }

  useEffect(() => {
    async function loadCompanies() {
      const data = await getCompanies();
      setCompanies(data.companies);
    }

    loadCompanies();
  }, []);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">
            Informações do veículo
          </h2>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="grid grid-cols-1 col-span-3 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <InputFormik
                label="Placa:"
                name="licensePlate"
                required={true}
                mask={maskLicensePlate}
              />

              <FipeBrandFieldFormik />

              <FipeModelFieldFormik />

              <FipeYearModelFieldFormik />

              <InputFormik
                label="Ano de fabricação:"
                name="year"
                required={true}
              />

              <InputFormik
                label="Código Renavam:"
                name="renavam"
                mask={maskRenavam}
                required={true}
              />

              <InputFormik
                label="Chassi:"
                name="chassi"
                mask={maskChassi}
                required={true}
              />

              <ComboBoxFormik
                label="Combustível:"
                name="fuel"
                required={true}
                options={Object.values(VEHICLE_FUEL).map((v) => ({
                  label: v,
                  value: v,
                }))}
              />

              {permissions?.includes("edit:editLocavibe") && (
                <InputFormik
                  label="Hodômetro:"
                  name="odometerLocavibe"
                  required={true}
                />
              )}

              <InputFormik
                label="Número do motor:"
                name="motorNumber"
                required={true}
              />

              <InputFormik
                label="Cor predominante:"
                name="color"
                required={true}
              />
              <div className="mb-2 mt-[-4px]">
                <label className="font-semibold text-sm">Titular:</label>
                <p className="border border-gray h-[43px] text-gray2 p-2 overflow-hidden rounded w-full bg-[#ffffff]">
                  {formik.values.partnerInfo.companyName}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <ComboBoxFormik
                  required={true}
                  name="companyId"
                  label="Base:"
                  options={companies
                    .filter(
                      (company) => company?.companyId && company?.companyName
                    ) // Filter out invalid entries
                    .map((company) => ({
                      value: company?.companyId as string, // Now guaranteed to be string
                      label: company?.companyName as string,
                    }))}
                  className="w-full"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <InputFormik
                  name="purchasePrice"
                  className="w-full"
                  label="Preço de compra:"
                  mask={maskCurrency}
                ></InputFormik>
              </div>

              {permissions?.includes("edit:editLocavibe") && (
                <div className=" ml-5 relative color flex items-center">
                  <label
                    htmlFor={`isEligibleSuaVibe`}
                    className="block text-sm font-medium text-gray-600"
                  >
                    {isEligibleSuaVibe ? (
                      <div className="flex gap-2 items-center justify-start text-greenPrimary">
                        <ImCheckboxChecked />
                        <span>Elegível à Sua vibe ?</span>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center justify-start">
                        <ImCheckboxUnchecked />
                        <span>Elegível à Sua vibe ?</span>
                      </div>
                    )}
                    <input
                      id="isEligibleSuaVibe"
                      name="isEligibleSuaVibe"
                      type="checkbox"
                      checked={isEligibleSuaVibe}
                      onChange={() => {
                        const newValue = !isEligibleSuaVibe; // Inverte o valor atual do estado
                        setIsEligibleSuaVibe(newValue); // Atualiza o estado local
                        formik.setFieldValue("isEligibleSuaVibe", newValue); // Atualiza o valor no Formik
                      }}
                      className="border p-2 rounded w-full absolute opacity-0"
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        </section>
        <section className="max-w-4xl mx-auto mb-3">
          <h2 className="text-2xl font-semibold mb-4">Integrações</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3  pb-4 lg:gap-4 lg:pb-0">
            <InputFormik
              label="Código Velotrack:"
              name="velotrackSettings.deviceId"
              readonly={true}
            />

            <div className="flex items-center">
              <ButtonWithLoader
                type="button"
                isLoading={bindVelotrackInProgress}
                onClick={handleBindWithVelotrack}
              >
                Buscar placa na Velotrack
              </ButtonWithLoader>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 pb-4 lg:gap-4 lg:pb-0">
            <InputFormik
              label="Código Fipe:"
              name="fipeInfo.fipeVehicleCode"
              readonly={true}
            />

            <div className="flex items-center">
              <ButtonWithLoader
                type="button"
                isLoading={bindFipeInProgress}
                onClick={handleBindFipeData}
              >
                Buscar código FIPE
              </ButtonWithLoader>
            </div>
          </div>
          <div>
            <IlevaActivate
              vehicleId={formik.values._id || ""}
              insuranceStatus={formik.values.insuranceStatus || ""}
            />
          </div>
        </section>
        <section className="max-w-4xl mx-auto">
          <div className="w-full dark:text-black bg-gray/40 rounded my-4 p-3">
            <h2 className="font-semibold text-2xl mb-4">Observações</h2>
            <TextareaFormik
              label=""
              name={`observations.${currentObservation}.text`}
              className="w-[90%] min-h-64 rounded shadow-md focus:border-white"
            />
          </div>
        </section>

        {/* Botão de Envio */}
        <div className="flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Enviar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditVehicle;
